import { Star } from 'lucide-react';
import React from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@components/ui/badge';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle
} from '@components/ui/card';
import { useError } from '@components/hooks/useError';
import { Button } from '@components/ui/button';
import { Api } from '@core/api';
import {
    AppRatingStatsDto,
    MsPageCatalogAppFeedbackDto
} from '@core/api/MobileClient';
import { links } from '@core/router';
import { injectTSDI } from '@core/tsdi';

const KIND_BADGE = {
    IDEA: { label: 'Idea', variant: 'success' },
    PROBLEM: { label: 'Problem', variant: 'destructive' },
    OTHER: { label: 'Feedback', variant: 'secondary' }
} as const;

export function RatingCard({ rating }: { rating?: AppRatingStatsDto }) {
    const count = rating?.count ?? 0;
    const histogram = rating?.histogram ?? [0, 0, 0, 0, 0];
    return (
        <Card>
            <CardHeader>
                <CardTitle>Ratings</CardTitle>
                <CardDescription>
                    {count === 0
                        ? 'No ratings yet.'
                        : `${rating?.average ?? '—'} ★ from ${count} rating${count === 1 ? '' : 's'}`}
                </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-1">
                {[5, 4, 3, 2, 1].map((stars) => {
                    const ratings = histogram[stars - 1] ?? 0;
                    return (
                        <div
                            key={stars}
                            className="flex items-center gap-2 text-sm"
                        >
                            <span className="flex w-8 items-center gap-0.5">
                                {stars}
                                <Star className="size-3" />
                            </span>
                            <div className="h-2 flex-1 overflow-hidden rounded bg-muted">
                                <div
                                    className="h-full bg-primary"
                                    style={{
                                        width: `${count ? (ratings / count) * 100 : 0}%`
                                    }}
                                />
                            </div>
                            <span className="w-10 text-right text-muted-foreground">
                                {ratings}
                            </span>
                        </div>
                    );
                })}
            </CardContent>
        </Card>
    );
}

const PAGE_SIZE = 20;

export function FeedbackCard({ app }: { app: string }) {
    const { mobileClient } = injectTSDI(Api);
    const showError = useError();
    const [page, setPage] = React.useState(0);
    const [result, setResult] = React.useState<MsPageCatalogAppFeedbackDto>();
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => setPage(0), [app]);

    React.useEffect(() => {
        let current = true;
        setLoading(true);
        mobileClient.supportApi
            .catalogAppSupportControllerGetCatalogAppFeedback(app, {
                page,
                size: PAGE_SIZE
            })
            .then((response) => current && setResult(response.data))
            .catch((e) => current && showError(e))
            .finally(() => current && setLoading(false));
        return () => {
            current = false;
        };
    }, [app, page]);

    const feedback = result?.content ?? [];
    const totalPages = result?.totalPages ?? 0;

    return (
        <Card>
            <CardHeader>
                <CardTitle>Feedback</CardTitle>
                <CardDescription>
                    What users sent from the app's store page, newest first.
                    {result ? ` ${result.totalElements} in total.` : ''}
                </CardDescription>
            </CardHeader>
            <CardContent>
                {feedback.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                        No feedback yet.
                    </p>
                ) : (
                    <div className="flex flex-col divide-y divide-border">
                        {feedback.map((item) => (
                            <div
                                key={item.id}
                                className="flex flex-col gap-1 py-3"
                            >
                                <div className="flex flex-wrap items-center gap-2 text-sm">
                                    <Badge
                                        variant={KIND_BADGE[item.kind].variant}
                                    >
                                        {KIND_BADGE[item.kind].label}
                                    </Badge>
                                    <Link
                                        className="hover:underline"
                                        to={links.userDetails(
                                            String(item.userId)
                                        )}
                                    >
                                        {[item.firstName, item.lastName]
                                            .filter(Boolean)
                                            .join(' ') || '—'}{' '}
                                        <span className="text-muted-foreground">
                                            #{item.userId}
                                        </span>
                                    </Link>
                                    <span className="text-muted-foreground">
                                        {new Date(
                                            item.createdDate
                                        ).toLocaleString()}
                                    </span>
                                </div>
                                <p className="whitespace-pre-wrap text-sm">
                                    {item.text}
                                </p>
                                {Object.keys(item.context).length > 0 && (
                                    <p className="text-xs text-muted-foreground">
                                        {Object.entries(item.context)
                                            .map(
                                                ([key, value]) =>
                                                    `${key} ${value}`
                                            )
                                            .join(' · ')}
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                )}
                {totalPages > 1 && (
                    <div className="mt-4 flex items-center justify-between border-t border-border pt-4 text-sm">
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={loading || page === 0}
                            onClick={() => setPage(page - 1)}
                        >
                            Newer
                        </Button>
                        <span className="text-muted-foreground">
                            Page {page + 1} of {totalPages}
                        </span>
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={loading || page + 1 >= totalPages}
                            onClick={() => setPage(page + 1)}
                        >
                            Older
                        </Button>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
